import type { LscMember, LscMemberIdentity } from './types';

export const CFB_ADVANCE_CADENCE_OPTIONS = [
  { value: '24_hours', label: '24 hours' },
  { value: '36_hours', label: '36 hours' },
  { value: '48_hours', label: '48 hours' },
  { value: '72_hours', label: '72 hours' },
  { value: 'other', label: 'Other' },
] as const;

export const CFB_PLATFORM_POLICY_OPTIONS = [
  { value: 'crossplay', label: 'Crossplay' },
  { value: 'xbox_only', label: 'Xbox only' },
  { value: 'playstation_only', label: 'PlayStation only' },
  { value: 'platform_divisions', label: 'Platform-specific divisions' },
  { value: 'manual_unknown', label: 'Manual / unknown' },
  { value: 'other', label: 'Other' },
] as const;

export const CFB_PRIMARY_PLATFORM_OPTIONS = [
  { value: 'xbox', label: 'Xbox' },
  { value: 'playstation', label: 'PlayStation' },
  { value: 'ea', label: 'EA' },
  { value: 'steam', label: 'Steam' },
  { value: 'epic', label: 'Epic' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Unknown' },
] as const;

export type CfbAdvanceCadenceSelect = (typeof CFB_ADVANCE_CADENCE_OPTIONS)[number]['value'];
export type CfbPlatformPolicySelect = (typeof CFB_PLATFORM_POLICY_OPTIONS)[number]['value'];
export type CfbPrimaryPlatform = (typeof CFB_PRIMARY_PLATFORM_OPTIONS)[number]['value'];

const KNOWN_ADVANCE_CADENCE = new Set<string>(
  CFB_ADVANCE_CADENCE_OPTIONS.filter((item) => item.value !== 'other').map((item) => item.value)
);

const KNOWN_PLATFORM_POLICIES = new Set<string>(
  CFB_PLATFORM_POLICY_OPTIONS.filter((item) => item.value !== 'other').map((item) => item.value)
);

export function parseAdvanceCadence(value: string): {
  select: CfbAdvanceCadenceSelect;
  custom: string;
} {
  const normalized = String(value || '').trim();
  if (KNOWN_ADVANCE_CADENCE.has(normalized)) {
    return { select: normalized as CfbAdvanceCadenceSelect, custom: '' };
  }
  if (normalized.startsWith('custom:')) {
    return { select: 'other', custom: normalized.slice('custom:'.length).trim() };
  }
  if (normalized) {
    return { select: 'other', custom: normalized };
  }
  return { select: '48_hours', custom: '' };
}

export function serializeAdvanceCadence(select: CfbAdvanceCadenceSelect, custom: string): string {
  if (select === 'other') {
    const trimmed = custom.trim();
    return trimmed ? `custom:${trimmed}` : 'custom:';
  }
  return select;
}

export function parsePlatformPolicy(value: string): {
  select: CfbPlatformPolicySelect;
  custom: string;
} {
  const normalized = String(value || '').trim();
  if (KNOWN_PLATFORM_POLICIES.has(normalized)) {
    return { select: normalized as CfbPlatformPolicySelect, custom: '' };
  }
  if (normalized.startsWith('custom:')) {
    return { select: 'other', custom: normalized.slice('custom:'.length).trim() };
  }
  if (normalized) {
    return { select: 'other', custom: normalized };
  }
  return { select: 'crossplay', custom: '' };
}

export function serializePlatformPolicy(select: CfbPlatformPolicySelect, custom: string): string {
  if (select === 'other') {
    const trimmed = custom.trim();
    return trimmed ? `custom:${trimmed}` : 'custom:';
  }
  return select;
}

export function deriveCrossplayEnabled(platformPolicy: string): boolean {
  const normalized = String(platformPolicy || '').trim().toLowerCase();
  return normalized === 'crossplay';
}

function identityValue(identities: LscMemberIdentity[] | undefined, type: string): string | null {
  const match = (identities ?? []).find(
    (item) => item.identity_type === type && String(item.identity_value || '').trim()
  );
  return match ? String(match.identity_value).trim() : null;
}

function readString(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  const text = String(value).trim();
  return text || null;
}

function metadataRecord(member: LscMember): Record<string, unknown> {
  const metadata = member.source_metadata;
  if (!metadata || typeof metadata !== 'object') {
    return {};
  }
  return metadata as Record<string, unknown>;
}

function metadataNested(record: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = record[key];
  if (!value || typeof value !== 'object') {
    return {};
  }
  return value as Record<string, unknown>;
}

function identityValues(identities: LscMemberIdentity[] | undefined, types: string[]): string | null {
  for (const type of types) {
    const value = identityValue(identities, type);
    if (value) {
      return value;
    }
  }
  return null;
}

function neonsportzField(member: LscMember, field: string): string | null {
  const neonsportz = metadataNested(metadataRecord(member), 'neonsportz');
  return readString(neonsportz[field]);
}

export function extractMemberDiscordDisplay(member: LscMember | null | undefined): {
  connected: boolean;
  handle: string | null;
  storedValue: string;
} {
  if (!member) {
    return { connected: false, handle: null, storedValue: '' };
  }

  const meta = metadataRecord(member);
  const neonsportz = metadataNested(meta, 'neonsportz');
  const discordMeta = metadataNested(meta, 'discord');
  const neonsportzDiscord = metadataNested(neonsportz, 'discord');

  const handle =
    readString(member.discord_handle) ||
    readString(member.discord_username) ||
    identityValues(member.identities, ['discord_username', 'discord_handle']) ||
    readString(meta.discord_handle) ||
    readString(meta.discord_username) ||
    neonsportzField(member, 'discord_username') ||
    neonsportzField(member, 'discord_handle') ||
    readString(discordMeta.handle) ||
    readString(discordMeta.username) ||
    readString(neonsportzDiscord.handle) ||
    readString(neonsportzDiscord.username) ||
    null;

  const discordId =
    readString(member.discord_id) ||
    readString((member as { discord_user_id?: string | null }).discord_user_id) ||
    identityValues(member.identities, ['discord_id', 'discord_user_id']) ||
    readString(meta.discord_id) ||
    readString(meta.discord_user_id) ||
    neonsportzField(member, 'discord_id') ||
    readString(discordMeta.user_id) ||
    readString(discordMeta.id) ||
    readString(neonsportzDiscord.user_id) ||
    readString(neonsportzDiscord.id) ||
    null;

  const storedValue = handle || discordId || '';
  return {
    connected: Boolean(handle || discordId),
    handle,
    storedValue,
  };
}

export function extractMemberTwitchLogin(member: LscMember | null | undefined): string {
  if (!member) {
    return '';
  }
  return String(member.twitch_login || member.provider_login || '').trim();
}

const INTERNAL_MEMBER_ID_PATTERN = /^member-[0-9a-f-]{36}$/i;

function isInternalMemberId(value: string): boolean {
  return INTERNAL_MEMBER_ID_PATTERN.test(value.trim());
}

function readCustomerSafeLabel(value: string | null | undefined): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed || isInternalMemberId(trimmed)) {
    return '';
  }
  return trimmed;
}

export function resolveMemberDisplayLabel(options: {
  member?: LscMember | null;
  assignment?: { twitch_login?: string | null; discord_handle?: string | null } | null;
}): string {
  const member = options.member;
  if (member) {
    const displayName = readCustomerSafeLabel(member.display_name);
    if (displayName) {
      return displayName;
    }
    const twitch = extractMemberTwitchLogin(member);
    if (twitch) {
      return twitch;
    }
    const discord = extractMemberDiscordDisplay(member);
    if (discord.connected && discord.handle) {
      return discord.handle;
    }
    const userUid = readCustomerSafeLabel(member.user_uid);
    if (userUid) {
      return userUid;
    }
  }

  const assignment = options.assignment;
  if (assignment) {
    const twitch = readCustomerSafeLabel(assignment.twitch_login);
    if (twitch) {
      return twitch;
    }
    const discord = readCustomerSafeLabel(assignment.discord_handle);
    if (discord) {
      return discord;
    }
  }

  return 'Unknown member';
}

export function formatCfbCompletenessCustomerMessage(issue: {
  code: string;
  message: string;
}): string {
  if (issue.code === 'missing_school_name_alias') {
    return 'School matching confidence: needs confirmation';
  }
  if (issue.code === 'missing_school_abbreviation_alias') {
    return 'Additional school matching confirmation needed.';
  }
  return issue.message;
}

export function platformIdLabel(platform: CfbPrimaryPlatform): string {
  switch (platform) {
    case 'xbox':
      return 'Xbox gamertag';
    case 'playstation':
      return 'PSN ID';
    case 'ea':
      return 'EA ID';
    case 'other':
      return 'Platform handle';
    default:
      return 'Platform ID / gamertag';
  }
}

export function inferPrimaryPlatformFromAssignment(assignment: {
  platform?: string | null;
  xbox_gamertag?: string | null;
  psn_id?: string | null;
  ea_id?: string | null;
  steam_id?: string | null;
  epic_id?: string | null;
  platform_handle?: string | null;
}): { platform: CfbPrimaryPlatform; platformId: string } {
  if (assignment.xbox_gamertag) {
    return { platform: 'xbox', platformId: assignment.xbox_gamertag };
  }
  if (assignment.psn_id) {
    return { platform: 'playstation', platformId: assignment.psn_id };
  }
  if (assignment.ea_id) {
    return { platform: 'ea', platformId: assignment.ea_id };
  }
  if (assignment.steam_id) {
    return { platform: 'steam', platformId: assignment.steam_id };
  }
  if (assignment.epic_id) {
    return { platform: 'epic', platformId: assignment.epic_id };
  }
  if (assignment.platform_handle) {
    return { platform: 'other', platformId: assignment.platform_handle };
  }
  const rawPlatform = String(assignment.platform || '').trim().toLowerCase();
  if (rawPlatform.includes('xbox')) {
    return { platform: 'xbox', platformId: '' };
  }
  if (rawPlatform.includes('playstation') || rawPlatform === 'psn') {
    return { platform: 'playstation', platformId: '' };
  }
  if (rawPlatform.includes('ea')) {
    return { platform: 'ea', platformId: '' };
  }
  return { platform: 'unknown', platformId: '' };
}

export function applyPrimaryPlatformToPayload(
  payload: {
    platform?: string;
    platform_handle?: string;
    ea_id?: string;
    xbox_gamertag?: string;
    psn_id?: string;
    steam_id?: string;
    epic_id?: string;
  },
  platform: CfbPrimaryPlatform,
  platformId: string
): void {
  payload.platform_handle = '';
  payload.ea_id = '';
  payload.xbox_gamertag = '';
  payload.psn_id = '';
  payload.steam_id = '';
  payload.epic_id = '';

  const trimmed = platformId.trim();
  switch (platform) {
    case 'xbox':
      payload.platform = 'Xbox';
      payload.xbox_gamertag = trimmed;
      break;
    case 'playstation':
      payload.platform = 'PlayStation';
      payload.psn_id = trimmed;
      break;
    case 'ea':
      payload.platform = 'EA';
      payload.ea_id = trimmed;
      break;
    case 'steam':
      payload.platform = 'Steam';
      payload.steam_id = trimmed;
      break;
    case 'epic':
      payload.platform = 'Epic';
      payload.epic_id = trimmed;
      break;
    case 'other':
      payload.platform = 'Other';
      payload.platform_handle = trimmed;
      break;
    default:
      payload.platform = 'Unknown';
      break;
  }
}

export function normalizeSchoolSearchTerm(value: string): string {
  return value.trim().toLowerCase();
}

export function getActiveAssignedMemberIds(
  assignments: Array<{ member_id: string; active_status?: string | null }>
): Set<string> {
  return new Set(
    assignments
      .filter((item) => (item.active_status ?? 'active') === 'active')
      .map((item) => item.member_id)
      .filter(Boolean)
  );
}

export function getUnassignedMembersForAdd(
  activeMembers: LscMember[],
  assignments: Array<{ member_id: string; active_status?: string | null }>
): LscMember[] {
  const assigned = getActiveAssignedMemberIds(assignments);
  return activeMembers.filter((member) => !assigned.has(member.id));
}

export function assignmentToFormPayload(assignment: {
  member_id: string;
  school_id?: string | null;
  school_name?: string | null;
  school_abbreviation?: string | null;
  mascot?: string | null;
  conference?: string | null;
  platform?: string | null;
  platform_handle?: string | null;
  ea_id?: string | null;
  xbox_gamertag?: string | null;
  psn_id?: string | null;
  steam_id?: string | null;
  epic_id?: string | null;
  twitch_login?: string | null;
  discord_handle?: string | null;
  is_user_controlled?: boolean;
  assignment_confidence?: string;
}): {
  member_id: string;
  school_name: string;
  school_abbreviation: string;
  mascot: string;
  conference: string;
  platform: string;
  platform_handle: string;
  ea_id: string;
  xbox_gamertag: string;
  psn_id: string;
  steam_id: string;
  epic_id: string;
  twitch_login: string;
  discord_handle: string;
  is_user_controlled: boolean;
  assignment_confidence: string;
  school_id?: string;
} {
  return {
    member_id: assignment.member_id,
    school_id: assignment.school_id ?? undefined,
    school_name: assignment.school_name ?? '',
    school_abbreviation: assignment.school_abbreviation ?? '',
    mascot: assignment.mascot ?? '',
    conference: assignment.conference ?? '',
    platform: assignment.platform ?? '',
    platform_handle: assignment.platform_handle ?? '',
    ea_id: assignment.ea_id ?? '',
    xbox_gamertag: assignment.xbox_gamertag ?? '',
    psn_id: assignment.psn_id ?? '',
    steam_id: assignment.steam_id ?? '',
    epic_id: assignment.epic_id ?? '',
    twitch_login: assignment.twitch_login ?? '',
    discord_handle: assignment.discord_handle ?? '',
    is_user_controlled: assignment.is_user_controlled ?? true,
    assignment_confidence: assignment.assignment_confidence ?? 'commissioner_confirmed',
  };
}

export function buildAssignmentUpdatePayload(
  form: ReturnType<typeof assignmentToFormPayload>,
  primaryPlatform: CfbPrimaryPlatform,
  platformId: string,
  selectedSchool: { school_id: string; athletic_name: string; initials: string; conference_name: string } | null,
  manualSchoolOpen: boolean
): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...form };
  delete payload.member_id;

  applyPrimaryPlatformToPayload(payload as Parameters<typeof applyPrimaryPlatformToPayload>[0], primaryPlatform, platformId);

  if (selectedSchool) {
    payload.school_id = selectedSchool.school_id;
    payload.school_name = selectedSchool.athletic_name;
    payload.school_abbreviation = selectedSchool.initials;
    payload.conference = selectedSchool.conference_name;
  } else if (manualSchoolOpen || form.school_name) {
    delete payload.school_id;
  }

  return payload;
}

export function schoolMatchesQuery(
  school: {
    display_name: string;
    athletic_name: string;
    short_name: string;
    initials: string;
    conference_name: string;
    aliases: string[];
  },
  query: string
): boolean {
  const normalized = normalizeSchoolSearchTerm(query);
  if (!normalized) {
    return true;
  }
  const tokens = [
    school.display_name,
    school.athletic_name,
    school.short_name,
    school.initials,
    school.conference_name,
    ...school.aliases,
  ]
    .map((item) => item.toLowerCase())
    .filter(Boolean);

  return tokens.some(
    (token) => token.includes(normalized) || normalized.includes(token) || token.startsWith(normalized)
  );
}
